import { useState, useEffect, useRef } from 'react';

const DEBOUNCE_MS = 350;
const MIN_CHARS = 2;
const OL_FIELDS = 'key,title,author_name,cover_i,first_publish_year';

async function searchOpenLibrary(query, signal) {
  // Try title-specific search first; fall back to broad search if sparse
  const titleParams = new URLSearchParams({ title: query, limit: 12, fields: OL_FIELDS });
  const res = await fetch(`https://openlibrary.org/search.json?${titleParams}`, { signal });
  let data = await res.json();

  if ((data.docs || []).length < 3) {
    const qParams = new URLSearchParams({ q: query, limit: 12, fields: OL_FIELDS });
    const res2 = await fetch(`https://openlibrary.org/search.json?${qParams}`, { signal });
    const data2 = await res2.json();
    const titleKeys = new Set((data.docs || []).map(d => d.key));
    const extra = (data2.docs || []).filter(d => !titleKeys.has(d.key));
    data = { docs: [...(data.docs || []), ...extra] };
  }

  return (data.docs || []).map(doc => ({
    key: doc.key,
    title: doc.title,
    author: doc.author_name?.[0] ?? 'Unknown author',
    coverId: doc.cover_i ?? null,
    coverUrl: doc.cover_i ? `https://covers.openlibrary.org/b/id/${doc.cover_i}-S.jpg` : null,
    year: doc.first_publish_year ?? null,
    source: 'openlibrary',
  }));
}

async function searchGoogleBooks(query, signal) {
  const key = process.env.REACT_APP_GOOGLE_BOOKS_KEY;
  const params = new URLSearchParams({
    q: `intitle:${query}`,
    maxResults: 10,
    printType: 'books',
    ...(key ? { key } : {}),
  });
  const res = await fetch(`https://www.googleapis.com/books/v1/volumes?${params}`, { signal });
  if (!res.ok) return [];
  const data = await res.json();
  return (data.items || []).map(item => {
    const info = item.volumeInfo;
    const thumbUrl = info.imageLinks?.thumbnail?.replace('http:', 'https:') ?? null;
    return {
      key: `gb:${item.id}`,
      title: info.title,
      author: info.authors?.[0] ?? 'Unknown author',
      coverId: null,
      coverUrl: thumbUrl,
      year: info.publishedDate ? parseInt(info.publishedDate) : null,
      source: 'googlebooks',
    };
  });
}

function deduplicateByTitle(results) {
  const seen = new Set();
  return results.filter(r => {
    const k = `${r.title?.toLowerCase()}|${r.author?.toLowerCase()}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

export function useBookSearch(query) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const abortRef = useRef(null);

  useEffect(() => {
    if (!query || query.length < MIN_CHARS) {
      setResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      if (abortRef.current) abortRef.current.abort();
      abortRef.current = new AbortController();
      const { signal } = abortRef.current;

      setLoading(true);
      try {
        // Run both sources in parallel
        const [olResults, gbResults] = await Promise.allSettled([
          searchOpenLibrary(query, signal),
          searchGoogleBooks(query, signal),
        ]);

        const ol = olResults.status === 'fulfilled' ? olResults.value : [];
        const gb = gbResults.status === 'fulfilled' ? gbResults.value : [];

        // OL results first; fill remaining slots from Google Books
        const merged = deduplicateByTitle([...ol, ...gb]).slice(0, 8);
        setResults(merged);
      } catch (err) {
        if (err.name !== 'AbortError') setResults([]);
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [query]);

  return { results, loading };
}
